// src/admin/pages/Cohorts.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminService } from "../../services/adminService";
import { Loader2 } from "lucide-react";

const Cohorts = () => {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [cohortData, courseData] = await Promise.all([
          adminService.getCohorts(),
          adminService.getCourses(),
        ]);
        setCohorts(cohortData);
        setCourses(courseData);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const courseMap = useMemo(() => {
    const map = {};
    courses.forEach((course) => {
      map[course.id] = course.title;
    });
    return map;
  }, [courses]);

  const filteredCohorts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cohorts;
    return cohorts.filter((c) => {
      const courseTitle = courseMap[c.course_id] || "";
      return (
        String(c.name || "").toLowerCase().includes(q) ||
        String(courseTitle).toLowerCase().includes(q)
      );
    });
  }, [cohorts, search, courseMap]);

  const stats = useMemo(() => {
    const total = cohorts.length;
    const active = cohorts.filter((c) => c.status === "active").length;
    const upcoming = cohorts.filter((c) => c.status === "upcoming").length;
    return { total, active, upcoming };
  }, [cohorts]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-[#54B0AF]">
        <Loader2 className="animate-spin" size={22} />
        Loading cohorts…
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-display">Cohorts</h1>

        <Link
          to="/admin/cohorts/create"
          className="px-4 py-2 bg-[#54B0AF] text-white rounded-md shadow hover:opacity-90 transition text-sm"
        >
          + Create Cohort
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Total cohorts" value={stats.total} />
        <StatCard label="Active" value={stats.active} />
        <StatCard label="Upcoming" value={stats.upcoming} />
      </div>

      <div className="bg-white rounded-lg shadow stakeholder-summary-card p-4 flex flex-col md:flex-row md:items-center gap-4">
        <input
          type="text"
          placeholder="Search cohort or course"
          className="border rounded-md px-3 py-2 text-sm w-full md:w-80"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="text-sm text-muted md:ml-auto">
          Showing {filteredCohorts.length} of {cohorts.length}
        </div>
      </div>

      {/* Cohorts Table */}
      <div className="bg-white rounded-lg shadow stakeholder-summary-card p-6">
        <table className="w-full border-collapse text-[15px]">
          <thead>
            <tr className="border-b bg-gray-50">
              <Th>Name</Th>
              <Th>Course</Th>
              <Th>Start Date</Th>
              <Th>Status</Th>
              <Th></Th>
            </tr>
          </thead>

          <tbody>
            {filteredCohorts.map((c) => (
              <tr
                key={c.id}
                className="border-b hover:bg-gray-100 transition"
              >
                <Td>{c.name}</Td>
                <Td>{courseMap[c.course_id] || c.course_id || "—"}</Td>
                <Td>{formatDate(c.start_date)}</Td>
                <Td className="capitalize">{c.status || "--"}</Td>

                <Td className="text-right">
                  <Link
                    to={`/admin/cohorts/${c.id}`}
                    className="text-[#54B0AF] font-semibold hover:underline"
                  >
                    View →
                  </Link>
                </Td>
              </tr>
            ))}

            {filteredCohorts.length === 0 && (
              <tr>
                <td
                  colSpan="5"
                  className="text-center text-muted italic py-6"
                >
                  No cohorts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Cohorts;


/* ---------- Styled Subcomponents ---------- */
const Th = ({ children }) => (
  <th className="p-3 text-left text-title font-semibold">{children}</th>
);

const Td = ({ children, className = "" }) => (
  <td className={`p-3 ${className}`}>{children}</td>
);

const StatCard = ({ label, value }) => (
  <div className="bg-white shadow rounded-lg p-5 text-center stakeholder-summary-card">
    <p className="text-muted text-sm">{label}</p>
    <p className="text-2xl font-semibold text-[#323232] mt-1">{value}</p>
  </div>
);

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString();
};
